import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  usePagination,
  useSortBy,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import moment from "moment";
import Select from "react-select";

const LedgerApprovalReport = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [sortType, setSortType] = useState("created_latest");


  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiClient.get(SERVER_BASE + "/ledgerMasters", {
          params: { centerCode: code },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data || []);
      } catch (err) {
        console.error("Error fetching items:", err);
      }
    };
    fetchData();
  }, []);

  const peopleOptions = useMemo(() => {
    const map = new Map();
  
    data.forEach((item) => {
      if (item.createdByName && item.createdBy) {
        map.set(item.createdBy, item.createdByName);
      }
      if (item.updatedByName && item.updatedBy) {
        map.set(item.updatedBy, item.updatedByName);
      }
      if (item.approvedByName && item.approvedBy) {
        map.set(item.approvedBy, item.approvedByName);
      }
    });
  
    return Array.from(map.entries()).map(([email, name]) => ({
      label: `${name} (${email})`,   // 👈 show both name + email
      value: email,                  // 👈 unique key for filtering
    }));
  }, [data]);
  
  
  const groupOptions = useMemo(() => {
    const setGroup = new Set();
  
    data.forEach((item) => {
      if (item.ledgerGroup) setGroup.add(item.ledgerGroup);
    });
  
    return Array.from(setGroup).map((g) => ({
      label: g,
      value: g,
    }));
  }, [data]);
  
const filteredData = useMemo(() => {
  const trimmed = search.trim().toLowerCase();

  let temp = data.filter((item) => {
    // -----------------------------------------
    // 1️⃣ PERSON FILTER
    // -----------------------------------------
    if (selectedPerson.length > 0) {
      const selectedEmails = selectedPerson.map((x) => x.value);

      const belongs =
        selectedEmails.includes(item.createdBy) ||
        selectedEmails.includes(item.updatedBy) ||
        selectedEmails.includes(item.approvedBy);

      if (!belongs) return false;
    }

    // -----------------------------------------
    // 2️⃣ GROUP FILTER
    // -----------------------------------------
    if (selectedGroup.length > 0) {
      const selectedGroups = selectedGroup.map((x) => x.value);
      if (!selectedGroups.includes(item.ledgerGroup)) return false;
    }

    // -----------------------------------------
    // 3️⃣ GLOBAL SEARCH
    // -----------------------------------------
    if (!trimmed) return true;

    const createdString = `${item.createdByName || ""} ${
      item.createdAt ? moment(item.createdAt).format("DD-MMM-YY hh:mm A") : ""
    }`.toLowerCase();

    const updatedString = `${item.updatedByName || ""} ${
      item.updatedAt ? moment(item.updatedAt).format("DD-MMM-YY hh:mm A") : ""
    }`.toLowerCase();

    const approvedString = `${item.approvedByName || ""} ${
      item.approvedAt ? moment(item.approvedAt).format("DD-MMM-YY hh:mm A") : ""
    }`.toLowerCase();

    return (
      (item.printName || "").toLowerCase().includes(trimmed) ||
      (item.ledgerGroup || "").toLowerCase().includes(trimmed) ||
      (item.centerType || "").toLowerCase().includes(trimmed) ||
      createdString.includes(trimmed) ||
      updatedString.includes(trimmed) ||
      approvedString.includes(trimmed)
    );
  });

  // -----------------------------------------
  // 4️⃣ ADVANCED SORTING (CREATED / UPDATED / APPROVED)
  // -----------------------------------------

  temp = temp.sort((a, b) => {
    const cA = new Date(a.createdAt || 0);
    const cB = new Date(b.createdAt || 0);

    const uA = new Date(a.updatedAt || 0);
    const uB = new Date(b.updatedAt || 0);

    const aA = new Date(a.approvedAt || 0);
    const aB = new Date(b.approvedAt || 0);

    switch (sortType) {
      // Created
      case "created_latest":
        return cB - cA;
      case "created_oldest":
        return cA - cB;

      // Updated
      case "updated_latest":
        return uB - uA;
      case "updated_oldest":
        return uA - uB;

      // Approved
      case "approved_latest":
        return aB - aA;
      case "approved_oldest":
        return aA - aB;

      default:
        return 0;
    }
  });

  return temp;
}, [search, data, selectedPerson, selectedGroup, sortType]);

  // Table Columns
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        accessor: "serial",
        Cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
        disableSortBy: true,
      },
      { Header: "Item Name", accessor: "printName" },
      { Header: "Center", accessor: "centerType" },
      { Header: "Group", accessor: "ledgerGroup" },
      {
        Header: "Created",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.createdByName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.createdAt
                ? moment(value.createdAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },

      {
        Header: "Updated",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.updatedByName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.updatedAt
                ? moment(value.updatedAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },

      {
        Header: "Approved",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.approvedByName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.approvedAt
                ? moment(value.approvedAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },
    ],
    []
  );

  // Table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    pageOptions,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between align-items-center">
        <h3>Ledger Approval Report</h3>
        <h6>Count: {filteredData.length}</h6>
      </div>

      <div className="d-flex gap-2 mb-3">
        {/* Search */}
        <input
          className="form-control"
          placeholder="Search anything..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

{/* React Select Filter for Created / Updated / Approved */}
<div style={{ minWidth: "350px" }}>
  <Select
    isMulti
    options={peopleOptions}
    value={selectedPerson}
    onChange={(selected) => setSelectedPerson(selected || [])}
    placeholder="Filter by Person"
  />
</div>
{/* Group Filter */}
<div style={{ minWidth: "250px" }}>
  <Select
    isMulti
    options={groupOptions}
    value={selectedGroup}
    onChange={(selected) => setSelectedGroup(selected || [])}
    placeholder="Filter by Group"
  />
</div>

{/* Sort By */}
<div style={{ minWidth: "100px" }}>
  <select
    className="form-select"
    value={sortType}
    onChange={(e) => setSortType(e.target.value)}
  >
    <option value="created_latest">Created → Latest</option>
    <option value="created_oldest">Created → Oldest</option>

    <option value="updated_latest">Updated → Latest</option>
    <option value="updated_oldest">Updated → Oldest</option>

    <option value="approved_latest">Approved → Latest</option>
    <option value="approved_oldest">Approved → Oldest</option>
  </select>
</div>


        {/* Page size */}
        <select
          className="form-select w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          <option value={50}>Show 50</option>
          <option value={75}>Show 75</option>
          <option value={100}>Show 100</option>
          <option value={200}>Show 200</option>
        </select>
      </div>

      {/* Table */}
      <table
        {...getTableProps()}
        className="table table-bordered table-striped"
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  style={{ cursor: "pointer" }}
                >
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default LedgerApprovalReport;
